/**
 * Offer session — latches a replacing global_coupon_code entry for the browser
 * session (first matching landing path wins). Overlay for prices, ATC stamps,
 * GWP meter hide, sale-dialog/nav/coupon copy, and cart discount swap.
 */
(function (global) {
  'use strict';

  var STORAGE_KEY = 'fd_offer_session';
  var PLAN_UPGRADE_KEY = 'fd_plan_upgrade';
  var SYNC_REQ = 'fd_offer_sync_req';
  var SYNC_RES = 'fd_offer_sync_res';
  var RESET_PARAM = 'fd_reset_offer';
  var RESET_BROADCAST = 'fd_offer_reset';
  var RESET_KEYS = [
    STORAGE_KEY,
    PLAN_UPGRADE_KEY,
    'fd_gwp_meter_landing',
    'fd_gwp_meter_hidden',
    'fd_gwp_meter_source',
  ];

  function readJsonEl() {
    var el = document.getElementById('fd-offer-catalog');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent || '{}');
    } catch (e) {
      return null;
    }
  }

  function readSession(key) {
    try {
      return sessionStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function writeSession(key, value) {
    try {
      sessionStorage.setItem(key, value);
    } catch (e) {}
  }

  function readCookie(name) {
    var prefix = name + '=';
    var parts = String(document.cookie || '').split(';');
    for (var i = 0; i < parts.length; i++) {
      var part = parts[i].replace(/^\s+/, '');
      if (part.indexOf(prefix) === 0) {
        try {
          return decodeURIComponent(part.slice(prefix.length));
        } catch (e) {
          return part.slice(prefix.length);
        }
      }
    }
    return null;
  }

  function writeSessionCookie(name, value) {
    var cookie = name + '=' + encodeURIComponent(value) + '; path=/; SameSite=Lax';
    if (global.location.protocol === 'https:') cookie += '; Secure';
    document.cookie = cookie;
  }

  function persistPayload(payload) {
    var raw = JSON.stringify(payload);
    writeSession(STORAGE_KEY, raw);
    writeSessionCookie(STORAGE_KEY, raw);
  }

  function parsePct(raw) {
    var n = parseInt(raw, 10);
    if (Number.isFinite(n) && n > 0 && n < 100) return n;
    return null;
  }

  function normalizePlanUpgradeConfig(opts) {
    var cfg = opts && typeof opts === 'object' ? opts : {};
    var percent = parsePct(cfg.percent);
    var savings = parsePct(cfg.savings);
    return {
      enabled: cfg.enabled !== false,
      code: String(cfg.code || '').trim(),
      percent: percent,
      savings: savings,
      toPlanId: String(cfg.toPlanId || cfg.to || '').trim(),
    };
  }

  function persistPlanUpgrade(cfg) {
    var payload = normalizePlanUpgradeConfig(cfg);
    var raw = JSON.stringify(payload);
    writeSession(PLAN_UPGRADE_KEY, raw);
    writeSessionCookie(PLAN_UPGRADE_KEY, raw);
    return payload;
  }

  function readPlanUpgrade() {
    var raw = readSession(PLAN_UPGRADE_KEY) || readCookie(PLAN_UPGRADE_KEY);
    if (!raw) return null;
    try {
      var parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return null;
      return normalizePlanUpgradeConfig(parsed);
    } catch (e) {
      return null;
    }
  }

  function schedulePlanUpgradeBackfill() {
    function run() {
      var cartApi = global.FDCartApi;
      if (!cartApi || typeof cartApi.backfillPlanUpgrade !== 'function') return;
      var pending = cartApi.getCart ? cartApi.getCart(false) : Promise.resolve(null);
      Promise.resolve(pending)
        .then(function (cart) {
          if (!cart) return null;
          return cartApi.backfillPlanUpgrade(cart);
        })
        .then(function (cart) {
          if (!cart) return;
          if (typeof global.updateCartDisplay === 'function') {
            return global.updateCartDisplay(cart, { skipPlanUpgradeBackfill: true });
          }
        })
        .catch(function () {});
    }
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () {
        global.setTimeout(run, 0);
      });
    } else {
      global.setTimeout(run, 0);
    }
  }

  function parseStored(raw) {
    if (!raw) return null;
    try {
      var data = JSON.parse(raw);
      if (data && data.handle) return data;
    } catch (e) {
      if (typeof raw === 'string' && raw.trim()) return { handle: raw.trim() };
    }
    return null;
  }

  function matchesAtSegmentBoundary(pathLower, needle) {
    var index = pathLower.indexOf(needle);
    while (index !== -1) {
      var next = pathLower.charAt(index + needle.length);
      if (next === '' || next === '/' || next === '?' || next === '#') return true;
      index = pathLower.indexOf(needle, index + 1);
    }
    return false;
  }

  function pathMatches(pathname, rawPath) {
    if (!rawPath || typeof rawPath !== 'string') return false;
    var pathLower = String(pathname || '').toLowerCase();
    var cleanPath = rawPath.startsWith('/') ? rawPath : '/' + rawPath;
    var cleanLower = cleanPath.toLowerCase();
    if (cleanLower.length > 1 && cleanLower.charAt(cleanLower.length - 1) === '/') {
      cleanLower = cleanLower.slice(0, -1);
    }
    if (cleanPath.indexOf('*') !== -1) {
      if (!cleanLower.replace(/[*/]/g, '')) return false;
      var regexPattern = cleanLower
        .replace(/[.+?^${}()|[\]\\]/g, '\\$&')
        .replace(/\*/g, '.*');
      return new RegExp('^' + regexPattern, 'i').test(pathLower);
    }
    return matchesAtSegmentBoundary(pathLower, cleanLower);
  }

  function offerMatchesPath(offer, pathname) {
    if (!offer || !Array.isArray(offer.activationPaths)) return false;
    return offer.activationPaths.some(function (p) {
      return pathMatches(pathname, p);
    });
  }

  function findByHandle(offers, handle) {
    var key = String(handle || '').trim();
    if (!key) return null;
    for (var i = 0; i < offers.length; i++) {
      if (offers[i] && offers[i].handle === key) return offers[i];
    }
    return null;
  }

  function findReplacingByPath(offers, pathname) {
    for (var i = 0; i < offers.length; i++) {
      var offer = offers[i];
      if (offer && offer.replacesGlobal && offerMatchesPath(offer, pathname)) return offer;
    }
    return null;
  }

  function findReplacingByCode(offers, code) {
    var key = String(code || '').trim().toUpperCase();
    if (!key) return null;
    for (var i = 0; i < offers.length; i++) {
      var offer = offers[i];
      if (!offer || !offer.replacesGlobal) continue;
      var sub = String(offer.subCode || '').trim().toUpperCase();
      var otp = String(offer.otpCode || '').trim().toUpperCase();
      if (sub === key || otp === key) return offer;
    }
    return null;
  }

  function findOfferByHandleOrCode(offers, value) {
    var offer = findByHandle(offers, value);
    if (offer) return offer;
    return findReplacingByCode(offers, value);
  }

  function queryFlag(params, name) {
    if (!params || !params.has(name)) return false;
    var raw = String(params.get(name) || '').trim().toLowerCase();
    return raw === '' || raw === '1' || raw === 'true' || raw === 'yes';
  }

  function hidesGwpMeter(offer) {
    if (!offer || !offer.replacesGlobal) return false;
    var mode = String(offer.gwpMode || '').toLowerCase();
    return mode === 'hide' || mode === 'immediate';
  }

  function applyGwpClass(hide) {
    var root = document.documentElement;
    if (!root) return;
    root.classList.toggle('fd-gwp-meter-hidden', !!hide);
    if (document.body) document.body.classList.toggle('fd-gwp-meter-hidden', !!hide);
  }

  function patchPdpOverride(offer) {
    if (!offer || !offer.replacesGlobal) return;
    var current = global.__pdpDiscountOverride;
    var o = current && typeof current === 'object' ? current : {};
    if (offer.subCode) o.couponCode = offer.subCode;
    if (offer.otpCode) o.otpCouponCode = offer.otpCode;
    if (Number.isFinite(offer.otpPct)) {
      o.discountPercent = offer.otpPct;
      o.discountMultiplier = (100 - offer.otpPct) / 100;
    }
    if (Number.isFinite(offer.subPct)) {
      o.subDiscountPercent = offer.subPct;
      o.subDiscountMultiplier = (100 - offer.subPct) / 100;
    }
    global.__pdpDiscountOverride = o;
    var carousel = global.__pbfcmCarouselDiscountOverride;
    if (carousel && typeof carousel === 'object') {
      if (offer.subCode) carousel.couponCode = offer.subCode;
      if (offer.otpCode) carousel.otpCouponCode = offer.otpCode;
      if (Number.isFinite(offer.otpPct)) carousel.discountPercent = offer.otpPct;
      if (Number.isFinite(offer.subPct)) carousel.subDiscountPercent = offer.subPct;
    }
  }

  function replaceCodeText(node, fromCode, toCode) {
    if (!node || !fromCode || !toCode) return;
    var from = String(fromCode);
    var to = String(toCode);
    if (!from || from.toUpperCase() === to.toUpperCase()) return;
    var walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT, null);
    var texts = [];
    while (walker.nextNode()) texts.push(walker.currentNode);
    var re = new RegExp(from.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
    var skipTag = { SCRIPT: 1, STYLE: 1, NOSCRIPT: 1, TEMPLATE: 1, CODE: 1 };
    texts.forEach(function (textNode) {
      var parent = textNode.parentElement;
      if (!parent || skipTag[parent.tagName]) return;
      if (parent.closest && parent.closest('script, style, noscript, template')) return;
      re.lastIndex = 0;
      if (textNode.nodeValue && re.test(textNode.nodeValue)) {
        re.lastIndex = 0;
        textNode.nodeValue = textNode.nodeValue.replace(re, to);
      }
    });
  }

  function patchCatalogBuyBox(offer) {
    if (!offer || !offer.replacesGlobal) return;
    if (offerMatchesPath(offer, (global.location && global.location.pathname) || '')) return;

    var subEl = document.getElementById('PBFCM-sub-discount-percentage');
    if (subEl && Number.isFinite(offer.subPct)) subEl.value = String(offer.subPct);

    var otpEl = document.getElementById('PBFCM-bfcm-discount-percent');
    if (otpEl && Number.isFinite(offer.otpPct)) otpEl.value = String(offer.otpPct);

    var subCode = String(offer.subCode || offer.otpCode || '').trim();
    var otpCode = String(offer.otpCode || offer.subCode || '').trim();
    document.querySelectorAll('[data-atc-coupon]').forEach(function (el) {
      var kind = el.getAttribute('data-option-kind') || el.getAttribute('data-frequency') || '';
      var isOtp = kind === 'otp' || kind === 'onetime';
      var next = isOtp ? otpCode : subCode;
      if (next) el.setAttribute('data-atc-coupon', next);
    });
    document.querySelectorAll('[data-display-discount-percent]').forEach(function (el) {
      if (isExplicitZeroDisplayPercent(el)) return;
      var kind = el.getAttribute('data-option-kind') || el.getAttribute('data-frequency') || '';
      var isOtp = kind === 'otp' || kind === 'onetime';
      var pct = isOtp ? offer.otpPct : offer.subPct;
      if (Number.isFinite(pct)) el.setAttribute('data-display-discount-percent', String(pct));
    });
  }

  function formatMoneyCents(cents) {
    var value = Number(cents);
    if (!Number.isFinite(value)) return '';
    return '$' + (value / 100).toFixed(2);
  }

  function setSalePriceEl(el, formatted) {
    if (!el || !formatted) return;
    var keep = [];
    el.querySelectorAll(
      '.PBFCM-PDP-frequency-selector__price-per-mo, .PBFCM-PDP-frequency-selector__alternate-price-after-mo'
    ).forEach(function (child) {
      keep.push(child.outerHTML);
    });
    var suffix = keep.join('');
    if (el.querySelector('.sr-only') || el.querySelector('[aria-hidden="true"]')) {
      el.innerHTML =
        '<span class="sr-only">Sale price ' + formatted + '</span>' +
        '<span aria-hidden="true">' + formatted + '</span>' +
        suffix;
    } else {
      el.innerHTML = formatted + suffix;
    }
  }

  function isExplicitZeroDisplayPercent(el) {
    if (!el || !el.hasAttribute('data-display-discount-percent')) return false;
    var raw = el.getAttribute('data-display-discount-percent');
    var n = parseFloat(raw);
    return (raw === '' || raw == null) || (Number.isFinite(n) && n === 0);
  }

  function selectedFrequencyIsExplicitZero() {
    return isExplicitZeroDisplayPercent(
      document.querySelector('.PBFCM-PDP-frequency-selector__option--selected')
    );
  }

  function repriceFrequencyOptions(offer) {
    document.querySelectorAll('.PBFCM-PDP-frequency-selector__option').forEach(function (opt) {
      if (isExplicitZeroDisplayPercent(opt)) return;
      var kind = opt.getAttribute('data-option-kind') || opt.getAttribute('data-frequency') || '';
      var isOtp = kind === 'otp' || kind === 'onetime';
      var pct = isOtp ? offer.otpPct : offer.subPct;
      if (!Number.isFinite(pct)) return;
      var retail = parseInt(
        opt.getAttribute('data-retail-unit-price') ||
          opt.getAttribute('data-unit-compare') ||
          opt.getAttribute('data-unit-price') ||
          '0',
        10
      );
      if (!Number.isFinite(retail) || retail <= 0) return;
      var sale = Math.round(retail * (100 - pct) / 100);
      opt.setAttribute('data-sale-unit-price', String(sale));
      if (opt.hasAttribute('data-unit-sale-price')) opt.setAttribute('data-unit-sale-price', String(sale));
      setSalePriceEl(opt.querySelector('.PBFCM-PDP-frequency-selector__price-final'), formatMoneyCents(sale));
    });
  }

  function repriceUpsellDatasets(offer) {
    document.querySelectorAll('.PBFCM-upsell-data[data-compare-price]').forEach(function (el) {
      var compare = parseInt(el.getAttribute('data-compare-price'), 10);
      if (!Number.isFinite(compare) || compare <= 0) return;
      if (Number.isFinite(offer.subPct)) {
        el.setAttribute('data-price', String(Math.round(compare * (100 - offer.subPct) / 100)));
      }
      if (Number.isFinite(offer.otpPct)) {
        el.setAttribute('data-onetime-price', String(Math.round(compare * (100 - offer.otpPct) / 100)));
      }
    });
    var otpSelected = !!(
      document.querySelector(
        '.PBFCM-PDP-frequency-selector__option--selected[data-option-kind="otp"], ' +
          '.PBFCM-PDP-frequency-selector__option--selected[data-frequency="onetime"], ' +
          '#PBFCM-radio-onetime:checked'
      )
    );
    var attr = otpSelected ? 'data-onetime-price' : 'data-price';
    document.querySelectorAll('.PBFCM-PDP-upsell-card').forEach(function (card) {
      var data = card.querySelector('.PBFCM-upsell-data');
      var priceEl = card.querySelector('.PBFCM-PDP-upsell-card__price-final');
      if (!data || !priceEl) return;
      var cents = parseInt(data.getAttribute(attr), 10);
      if (!Number.isFinite(cents)) return;
      setSalePriceEl(priceEl, formatMoneyCents(cents));
    });
  }

  function repriceTaggedPrices(offer) {
    document.querySelectorAll('[data-fd-offer-price][data-compare-cents]').forEach(function (el) {
      var cents = parseInt(el.getAttribute('data-compare-cents'), 10);
      if (!Number.isFinite(cents) || cents <= 0) return;
      var kind = el.getAttribute('data-fd-offer-price');
      var pct = kind === 'otp' ? offer.otpPct : offer.subPct;
      if (!Number.isFinite(pct)) return;
      var sale = Math.round(cents * (100 - pct) / 100);
      el.setAttribute('data-price-multiplier', String((100 - pct) / 100));
      el.textContent = formatMoneyCents(sale);
    });
  }

  function repriceCatalogSurfaces(offer) {
    if (!offer || !offer.replacesGlobal) return;
    if (offerMatchesPath(offer, (global.location && global.location.pathname) || '')) return;
    if (selectedFrequencyIsExplicitZero()) return;
    repriceFrequencyOptions(offer);
    repriceUpsellDatasets(offer);
    repriceTaggedPrices(offer);
    repriceUpsellDialog(offer);
  }

  function displayedOffPct(offer) {
    var raw = String((offer && offer.upToOff) || '').replace(/[^\d.]/g, '');
    var parsed = parseFloat(raw);
    if (Number.isFinite(parsed) && parsed > 0) return Math.round(parsed);
    var max = 0;
    [offer.subPct, offer.otpPct, offer.higherSubPct, offer.ninetyDayPct].forEach(function (n) {
      if (Number.isFinite(n) && n > max) max = n;
    });
    return max || null;
  }

  function replacePercentCopy(node, nextPct) {
    if (!node || !Number.isFinite(nextPct) || nextPct <= 0) return;
    var walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT, null);
    var texts = [];
    while (walker.nextNode()) texts.push(walker.currentNode);
    var re = /\b52(\s*%)/gi;
    var skipTag = { SCRIPT: 1, STYLE: 1, NOSCRIPT: 1, TEMPLATE: 1, CODE: 1 };
    texts.forEach(function (textNode) {
      var parent = textNode.parentElement;
      if (!parent || skipTag[parent.tagName]) return;
      if (parent.closest && parent.closest('script, style, noscript, template')) return;
      re.lastIndex = 0;
      if (textNode.nodeValue && re.test(textNode.nodeValue)) {
        re.lastIndex = 0;
        textNode.nodeValue = textNode.nodeValue.replace(re, String(nextPct) + '$1');
      }
    });
  }

  function codeForOfferNode(el, offer) {
    var kind = el.getAttribute('data-fd-offer-code-kind');
    if (!kind) {
      var opt = el.closest && el.closest('.PBFCM-PDP-frequency-selector__option');
      kind = opt && (opt.getAttribute('data-option-kind') || opt.getAttribute('data-frequency'));
    }
    var isOtp = kind === 'otp' || kind === 'onetime';
    return isOtp
      ? String(offer.otpCode || offer.subCode || '').trim()
      : String(offer.subCode || offer.otpCode || '').trim();
  }

  function hideSaleGwpCards(offer) {
    var hide = hidesGwpMeter(offer);
    document.querySelectorAll('.sale-offer-card').forEach(function (card) {
      var title = String((card.querySelector('.sale-offer-card__title') || {}).textContent || '');
      var isGwp = /hydration|electrolyte|\bgift\b/i.test(title);
      if (hide && isGwp) {
        card.hidden = true;
        card.setAttribute('data-fd-offer-gwp-hidden', 'true');
      } else if (card.getAttribute('data-fd-offer-gwp-hidden')) {
        card.hidden = false;
        card.removeAttribute('data-fd-offer-gwp-hidden');
      }
    });
  }

  function saleDialogOffersGrid() {
    return document.querySelector('.sale-dialog__offers');
  }

  function rememberDefaultSaleOffers(grid) {
    if (!grid || grid.getAttribute('data-fd-offer-default-ready') === 'true') return;
    grid.setAttribute('data-fd-offer-default-html', grid.innerHTML);
    grid.setAttribute('data-fd-offer-default-ready', 'true');
  }

  function restoreDefaultSaleOffers(grid) {
    if (!grid) return;
    rememberDefaultSaleOffers(grid);
    var html = grid.getAttribute('data-fd-offer-default-html');
    if (html != null) grid.innerHTML = html;
  }

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function renderSaleDialogCard(card, showImages) {
    var title = escapeHtml(card && card.title);
    var desc = (card && card.descriptionHtml) || '';
    var buttonText = String((card && card.buttonText) || '').trim();
    var buttonUrl = String((card && card.buttonUrl) || '').trim();
    var overlay = !!(card && card.buttonOverlay && card.image && showImages);
    var hasButton = !!(buttonText && buttonUrl);
    var classes = 'sale-offer-card' + (overlay ? ' sale-offer-card--button-overlay' : '');
    var imageHtml = '';
    if (showImages && card && card.image) {
      imageHtml =
        '<div class="sale-offer-card__image">' +
        '<img src="' + escapeHtml(card.image) + '" alt="' + escapeHtml(card.imageAlt || card.title || '') + '" loading="lazy">' +
        (overlay && hasButton
          ? '<a href="' + escapeHtml(buttonUrl) + '" class="sale-offer-card__button sale-offer-card__button--overlay">' +
            escapeHtml(buttonText) +
            '</a>'
          : '') +
        '</div>';
    }
    var buttonHtml =
      hasButton && !overlay
        ? '<a href="' + escapeHtml(buttonUrl) + '" class="sale-offer-card__button">' + escapeHtml(buttonText) + '</a>'
        : '';
    return (
      '<div class="' + classes + '" data-fd-offer-card="true">' +
      imageHtml +
      '<div class="sale-offer-card__content"><div class="sale-offer-card__text">' +
      (title ? '<h3 class="sale-offer-card__title">' + title + '</h3>' : '') +
      (desc ? '<div class="sale-offer-card__description">' + desc + '</div>' : '') +
      '</div>' +
      buttonHtml +
      '</div></div>'
    );
  }

  function applySaleDialogCards(offer) {
    var grid = saleDialogOffersGrid();
    if (!grid) return;
    rememberDefaultSaleOffers(grid);
    var cards = offer && Array.isArray(offer.saleDialogCards) ? offer.saleDialogCards : [];
    if (!offer || !offer.replacesGlobal || !cards.length) {
      restoreDefaultSaleOffers(grid);
      hideSaleGwpCards(offer);
      return;
    }
    var dialog = document.querySelector('sale-dialog');
    var showImages = !dialog || dialog.getAttribute('data-show-images') !== 'false';
    grid.innerHTML = cards.map(function (card) {
      return renderSaleDialogCard(card, showImages);
    }).join('');
  }

  function repriceUpsellDialog(offer) {
    if (!offer || !offer.replacesGlobal) return;
    if (selectedFrequencyIsExplicitZero()) return;
    var otpSelected = !!(
      document.querySelector(
        '.PBFCM-PDP-frequency-selector__option--selected[data-option-kind="otp"], ' +
          '.PBFCM-PDP-frequency-selector__option--selected[data-frequency="onetime"], ' +
          '#PBFCM-radio-onetime:checked'
      )
    );
    var pct = otpSelected ? offer.otpPct : offer.subPct;
    if (!Number.isFinite(pct)) return;
    document.querySelectorAll('.upsell-dialog__upsell-data[data-compare-price], .upsell-dialog_upsell-data[data-compare-price]').forEach(function (el) {
      var compare = parseInt(el.getAttribute('data-compare-price'), 10);
      if (!Number.isFinite(compare) || compare <= 0) return;
      var sale = Math.round(compare * (100 - pct) / 100);
      el.setAttribute('data-price', String(sale));
      var card = el.closest('.upsell-dialog__upsell-card');
      if (!card) return;
      var finalEl = card.querySelector('.upsell-dialog__upsell-price-final');
      if (finalEl) finalEl.textContent = formatMoneyCents(sale);
      var compareEl = card.querySelector('.upsell-dialog__upsell-price-compare');
      if (compareEl) {
        var showCompare = sale < compare;
        compareEl.hidden = !showCompare;
        if (showCompare) compareEl.style.removeProperty('display');
        else compareEl.style.setProperty('display', 'none');
      }
      var badge = card.querySelector('.upsell-dialog__upsell-discount-badge');
      if (badge) {
        if (pct > 0 && sale < compare) badge.textContent = String(pct) + '%';
        else badge.hidden = true;
      }
    });
  }

  function dismissLayerDialogs() {
    document.querySelectorAll('upsell-dialog').forEach(function (el) {
      try {
        if (typeof el.hide === 'function') el.hide();
      } catch (e) {}
      el.classList.remove('active');
      el.setAttribute('aria-hidden', 'true');
    });
    document.querySelectorAll('otp-convert-dialog, .otp-convert-dialog').forEach(function (el) {
      try {
        if (typeof el.hide === 'function') el.hide();
        if (typeof el.close === 'function') el.close();
      } catch (e) {}
      el.classList.remove('active', 'is-open', 'open');
      el.setAttribute('aria-hidden', 'true');
      if (el.style) el.style.display = 'none';
    });
    document.querySelectorAll('dialog[open]').forEach(function (el) {
      try {
        el.close();
      } catch (e) {}
    });
    if (document.body) document.body.classList.remove('upsell-dialog-open');
  }

  function applyCopy(offer, globalCode) {
    if (!offer || !offer.replacesGlobal) {
      applySaleDialogCards(offer);
      return;
    }
    patchCatalogBuyBox(offer);
    repriceCatalogSurfaces(offer);
    var heading = offer.saleDialogHeading;
    var body = offer.saleDialogBody;
    var headingEl = document.querySelector('[data-fd-offer-heading]') || document.querySelector('.sale-dialog__heading');
    var subEl = document.querySelector('[data-fd-offer-body]') || document.querySelector('.sale-dialog__subheading');
    if (heading && headingEl) headingEl.textContent = heading;
    if (body && subEl) subEl.textContent = body;
    else if (body && headingEl && headingEl.nextElementSibling && headingEl.nextElementSibling.tagName !== 'BUTTON') {
      headingEl.nextElementSibling.textContent = body;
    }

    var nextCode = offer.subCode || offer.otpCode;
    var dialog = document.querySelector('sale-dialog');
    applySaleDialogCards(offer);
    if (dialog) replaceCodeText(dialog, globalCode, nextCode);
    if (document.body) replaceCodeText(document.body, globalCode, nextCode);
    var offPct = displayedOffPct(offer);
    [dialog, document.querySelector('.pbfcm-coupon-banner'), document.querySelector('.desktop-nav-v3__banner')]
      .forEach(function (scope) {
        replacePercentCopy(scope, offPct);
      });
    document.querySelectorAll('[data-fd-offer-upto]').forEach(function (el) {
      replacePercentCopy(el, offPct);
    });

    document.querySelectorAll('[data-fd-offer-code], #PBFCM-coupon-code').forEach(function (el) {
      var next = codeForOfferNode(el, offer);
      if (next) el.textContent = next;
    });
    document.querySelectorAll('.desktop-nav-v3__banner-code, .pbfcm-coupon-banner__coupon-code').forEach(function (el) {
      if (offer.subCode) el.textContent = offer.subCode;
    });

    var bannerOffer = document.querySelector('.desktop-nav-v3__banner-offer span:not(.desktop-nav-v3__banner-code)');
    if (bannerOffer && offPct) {
      bannerOffer.textContent = 'Up To ' + offPct + '% code';
    }
  }

  function funnelCodesToDrop(offer, globalCode) {
    var drop = {};
    var globalKey = String(globalCode || '').trim().toUpperCase();
    if (globalKey) drop[globalKey] = true;
    var keep = {};
    [offer && offer.subCode, offer && offer.otpCode].forEach(function (code) {
      var key = String(code || '').trim().toUpperCase();
      if (key) keep[key] = true;
    });
    var offers = (global.FDOffer && global.FDOffer.catalog) || [];
    offers.forEach(function (entry) {
      if (!entry || !entry.replacesGlobal) return;
      [entry.subCode, entry.otpCode].forEach(function (code) {
        var key = String(code || '').trim().toUpperCase();
        if (key && !keep[key]) drop[key] = true;
      });
    });
    return drop;
  }

  // The code list is rewritten whole, so it is built only from a cart that matches
  // the server: the event's cart when it is current, else a read once writes end.
  function currentCartFor(knownCart) {
    var store = global.FDCart;
    if (knownCart && Array.isArray(knownCart.items) && store && typeof store.isCartCurrent === 'function' && store.isCartCurrent(knownCart)) {
      return Promise.resolve(knownCart);
    }
    var idle = store && typeof store.whenWritesIdle === 'function' ? store.whenWritesIdle(3000) : Promise.resolve();
    return idle.then(function () {
      var current = store && typeof store.getCurrentCart === 'function' ? store.getCurrentCart() : null;
      if (current) return current;
      if (global.FDCartApi && typeof global.FDCartApi.getCart === 'function') return global.FDCartApi.getCart(true);
      return fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' }).then(function (res) {
        return res.json();
      });
    });
  }

  function swapCartDiscount(offer, globalCode, knownCart) {
    if (!offer || !offer.replacesGlobal) return;
    var nextCode = String(offer.subCode || offer.otpCode || '').trim();
    if (!nextCode) return;
    var drop = funnelCodesToDrop(offer, globalCode);

    currentCartFor(knownCart)
      .then(function (cart) {
        if (!cart || !Array.isArray(cart.items) || !cart.items.length) return null;
        var existing = (cart.discount_codes || [])
          .map(function (entry) {
            return entry && entry.code;
          })
          .filter(Boolean);
        var kept = existing.filter(function (code) {
          return !drop[String(code).toUpperCase()];
        });
        var hasNext = kept.some(function (code) {
          return String(code).toUpperCase() === nextCode.toUpperCase();
        });
        if (!hasNext) kept.push(nextCode);
        var same =
          kept.length === existing.length &&
          kept.every(function (code, i) {
            return String(code).toUpperCase() === String(existing[i] || '').toUpperCase();
          });
        if (same && hasNext) return null;
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ discount: kept.join(',') }),
        }).then(function (res) {
          return res.json();
        });
      })
      .then(function (cart) {
        if (!cart || !Array.isArray(cart.items)) return;
        if (typeof global.updateCartDisplay === 'function') global.updateCartDisplay(cart);
        else if (global.FDCart) global.FDCart.setCart(cart);
      })
      .catch(function () {});
  }

  function hydrateFromCart(api, catalog) {
    if (!api || api.active && api.active.replacesGlobal) return;
    fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        if (!cart || !Array.isArray(cart.items) || !cart.items.length) return;
        var codes = (cart && cart.discount_codes) || [];
        for (var i = 0; i < codes.length; i++) {
          var offer = findReplacingByCode(catalog.offers || [], codes[i] && codes[i].code);
          if (offer) {
            persistPayload({ handle: offer.handle, path: catalog.currentPath || '' });
            api.active = offer;
            publish(api, catalog);
            return;
          }
        }
      })
      .catch(function () {});
  }

  function publish(api, catalog) {
    patchPdpOverride(api.active);
    applyGwpClass(hidesGwpMeter(api.active));
    function afterDom() {
      patchPdpOverride(api.active);
      applyCopy(api.active, catalog.globalCode);
      swapCartDiscount(api.active, catalog.globalCode);
      try {
        global.dispatchEvent(new CustomEvent('fd:offer-changed', { detail: { offer: api.active } }));
      } catch (e) {}
      if (typeof global.requestAnimationFrame === 'function') {
        global.requestAnimationFrame(function () {
          applyCopy(api.active, catalog.globalCode);
        });
      }
    }
    try {
      global.dispatchEvent(new CustomEvent('fd:offer-changed', { detail: { offer: api.active } }));
    } catch (e) {}
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', afterDom);
    } else {
      afterDom();
    }
  }

  function clearOfferKeys() {
    RESET_KEYS.forEach(function (key) {
      try {
        sessionStorage.removeItem(key);
      } catch (e) {}
      var expired = key + '=; path=/; SameSite=Lax; Max-Age=0';
      if (global.location.protocol === 'https:') expired += '; Secure';
      document.cookie = expired;
    });
  }

  function clearCartAndCodes() {
    if (global.FDCartApi && typeof global.FDCartApi.clearCart === 'function') {
      return global.FDCartApi.clearCart();
    }
    var post = function (url, body) {
      return fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(body),
      });
    };
    return post('/cart/clear.js', {}).then(function () {
      return post('/cart/update.js', { discount: '' });
    });
  }

  function resetOfferSession() {
    clearOfferKeys();
    try {
      localStorage.setItem(RESET_BROADCAST, String(Date.now()));
      localStorage.removeItem(RESET_BROADCAST);
    } catch (e) {}
    var params = new URLSearchParams(global.location.search);
    params.delete(RESET_PARAM);
    var query = params.toString();
    var target = global.location.pathname + (query ? '?' + query : '') + global.location.hash;
    function run() {
      clearCartAndCodes()
        .catch(function () {})
        .then(function () {
          global.location.replace(target);
        });
    }
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', run);
    } else {
      run();
    }
  }

  try {
    global.addEventListener('storage', function (event) {
      if (event.key === RESET_BROADCAST && event.newValue) clearOfferKeys();
    });
  } catch (e) {}

  if (new URLSearchParams(global.location.search).has(RESET_PARAM)) {
    resetOfferSession();
    return;
  }

  var catalog = readJsonEl() || { offers: [], defaultHandle: '', globalCode: '', currentPath: '' };
  catalog.offers = Array.isArray(catalog.offers) ? catalog.offers : [];

  var designMode = !!(global.Shopify && global.Shopify.designMode);
  var inTopWindow = true;
  try {
    inTopWindow = global.self === global.top;
  } catch (e) {
    inTopWindow = true;
  }

  var searchParams = new URLSearchParams(global.location.search);
  var queryHandle = String(searchParams.get('fd_offer') || searchParams.get('utm_fd_offer') || '').trim();
  var queryPlanUpgrade = queryFlag(searchParams, 'fd_plan_upgrade') || queryFlag(searchParams, 'utm_fd_plan_upgrade');
  var queryOffer = queryHandle ? findOfferByHandleOrCode(catalog.offers, queryHandle) : null;

  var stored = parseStored(readSession(STORAGE_KEY) || readCookie(STORAGE_KEY));
  var pathOffer =
    inTopWindow && !designMode ? findReplacingByPath(catalog.offers, catalog.currentPath) : null;
  var storedOffer = stored ? findOfferByHandleOrCode(catalog.offers, stored.handle) : null;
  var storedJs = !!(stored && stored.source === 'js' && storedOffer);
  var defaultOffer = findByHandle(catalog.offers, catalog.defaultHandle) || catalog.offers[0] || null;
  var planUpgrade = readPlanUpgrade();

  if (queryPlanUpgrade) {
    planUpgrade = persistPlanUpgrade({
      enabled: true,
      code: String(searchParams.get('fd_plan_upgrade_code') || '').trim(),
      percent: searchParams.get('fd_plan_upgrade_percent'),
      savings: searchParams.get('fd_plan_upgrade_savings'),
      toPlanId: String(searchParams.get('fd_plan_upgrade_to') || '').trim(),
    });
  }

  var active = defaultOffer;
  if (!designMode && inTopWindow) {
    // JS / query latch (Intelligems) sticks for the session and beats path matching.
    // Otherwise the landing page you are on wins (latest funnel decides).
    if (queryOffer) {
      active = queryOffer;
      persistPayload({ handle: queryOffer.handle, path: catalog.currentPath || '', source: 'js' });
      stored = { handle: queryOffer.handle, path: catalog.currentPath || '', source: 'js' };
      storedJs = true;
    } else if (storedJs) {
      active = storedOffer;
      persistPayload({ handle: storedOffer.handle, path: stored.path || catalog.currentPath || '', source: 'js' });
    } else if (pathOffer) {
      active = pathOffer;
      persistPayload({ handle: pathOffer.handle, path: catalog.currentPath || '', source: 'path' });
      stored = { handle: pathOffer.handle, path: catalog.currentPath || '', source: 'path' };
    } else if (storedOffer && storedOffer.replacesGlobal) {
      active = storedOffer;
      persistPayload({
        handle: storedOffer.handle,
        path: stored.path || catalog.currentPath || '',
        source: stored.source || 'path',
      });
    }
  } else if (pathOffer && designMode) {
    active = pathOffer;
  }

  function latchOffer(offer, source) {
    if (!offer || !offer.handle) return null;
    persistPayload({
      handle: offer.handle,
      path: catalog.currentPath || '',
      source: source || 'js',
    });
    api.active = offer;
    publish(api, catalog);
    return offer;
  }

  function activateOffer(handle, opts) {
    var extra = opts && typeof opts === 'object' ? opts : {};
    var value = handle;
    if (handle && typeof handle === 'object') {
      extra = handle;
      value = handle.handle || handle.code || '';
    }
    var offer = findOfferByHandleOrCode(catalog.offers, value);
    if (!offer) {
      console.warn('[FDOffer] unknown offer', value);
      return null;
    }
    latchOffer(offer, 'js');
    if (extra.planUpgrade) {
      enablePlanUpgradeApi(extra.planUpgrade === true ? extra : extra.planUpgrade);
    }
    return offer;
  }

  function enablePlanUpgradeApi(opts) {
    planUpgrade = persistPlanUpgrade(opts);
    try {
      global.dispatchEvent(new CustomEvent('fd:plan-upgrade-changed', { detail: planUpgrade }));
    } catch (e) {}
    schedulePlanUpgradeBackfill();
    return planUpgrade;
  }

  var api = {
    __native: true,
    catalog: catalog.offers,
    defaultHandle: catalog.defaultHandle,
    globalCode: catalog.globalCode,
    default: defaultOffer,
    active: active,
    hidesGwpMeter: hidesGwpMeter,
    patchPdpOverride: patchPdpOverride,
    matchesActivationPath: function (pathname) {
      return offerMatchesPath(api.active, pathname || (global.location && global.location.pathname) || '');
    },
    overridesCatalogPdp: function (pathname) {
      if (!api.active || !api.active.replacesGlobal) return false;
      return !offerMatchesPath(api.active, pathname || (global.location && global.location.pathname) || '');
    },
    applyCopy: function () {
      applyCopy(api.active, catalog.globalCode);
    },
    swapCartDiscount: function () {
      swapCartDiscount(api.active, catalog.globalCode);
    },
    dismissLayerDialogs: dismissLayerDialogs,
    activate: activateOffer,
    enablePlanUpgrade: enablePlanUpgradeApi,
    planUpgradeForced: function () {
      return !!(planUpgrade && planUpgrade.enabled);
    },
    planUpgradeConfig: function () {
      return planUpgrade;
    },
  };

  global.FDOffer = api;
  publish(api, catalog);

  if (planUpgrade && planUpgrade.enabled) {
    schedulePlanUpgradeBackfill();
  }

  if (!designMode && inTopWindow && !(api.active && api.active.replacesGlobal)) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function () {
        hydrateFromCart(api, catalog);
      });
    } else {
      hydrateFromCart(api, catalog);
    }
  }

  try {
    global.addEventListener('storage', function (event) {
      if (event.key === SYNC_REQ && event.newValue) {
        try {
          localStorage.setItem(SYNC_RES, readSession(STORAGE_KEY) || readCookie(STORAGE_KEY) || '');
          localStorage.removeItem(SYNC_RES);
        } catch (e) {}
        return;
      }
      if (event.key === SYNC_RES && event.newValue) {
        var incoming = parseStored(event.newValue);
        var offer = incoming ? findByHandle(catalog.offers, incoming.handle) : null;
        if (!offer || !offer.replacesGlobal) return;
        persistPayload(incoming);
        api.active = offer;
        publish(api, catalog);
      }
    });
    if (!designMode && !(storedOffer && storedOffer.replacesGlobal)) {
      localStorage.setItem(SYNC_REQ, String(Date.now()));
      localStorage.removeItem(SYNC_REQ);
    }
  } catch (e) {}

  document.addEventListener('shopify:section:load', function () {
    applyCopy(api.active, catalog.globalCode);
    applyGwpClass(hidesGwpMeter(api.active));
    patchPdpOverride(api.active);
  });

  var swapTimer = null;
  document.addEventListener('cart:updated', function (event) {
    var eventCart = event && event.detail && event.detail.cart;
    if (swapTimer) global.clearTimeout(swapTimer);
    swapTimer = global.setTimeout(function () {
      applyCopy(api.active, catalog.globalCode);
      swapCartDiscount(api.active, catalog.globalCode, eventCart);
    }, 50);
  });

  document.addEventListener('upsell-dialog:opened', function () {
    applyCopy(api.active, catalog.globalCode);
  });

  var IG_PLAN_CLASS = 'fd-plan-upgrade';
  var IG_ALIAS_CLASS = 'fd-ig-90-day';
  var IG_OFFER_PREFIX = 'fd-offer-';
  var IG_DEFAULT_HANDLE = '90-day-funnel';
  var igClassApplied = '';

  function classListTokens(el) {
    var out = [];
    if (!el || !el.classList) return out;
    for (var i = 0; i < el.classList.length; i++) out.push(el.classList[i]);
    return out;
  }

  function attrOnRoots(name) {
    var html = document.documentElement;
    var body = document.body;
    var fromHtml = html && html.getAttribute && html.getAttribute(name);
    if (fromHtml != null && String(fromHtml).trim()) return String(fromHtml).trim();
    var fromBody = body && body.getAttribute && body.getAttribute(name);
    if (fromBody != null && String(fromBody).trim()) return String(fromBody).trim();
    return '';
  }

  function readIntelligemsTrigger() {
    var tokens = classListTokens(document.documentElement).concat(classListTokens(document.body));
    var handle = attrOnRoots('data-fd-offer');
    var planAttr = attrOnRoots('data-fd-plan-upgrade').toLowerCase();
    var planUpgrade = planAttr === '1' || planAttr === 'true' || planAttr === 'yes';
    var i;
    for (i = 0; i < tokens.length; i++) {
      var cls = tokens[i];
      if (cls === IG_PLAN_CLASS || cls === IG_ALIAS_CLASS) planUpgrade = true;
      if (cls === IG_ALIAS_CLASS && !handle) handle = IG_DEFAULT_HANDLE;
      if (cls.indexOf(IG_OFFER_PREFIX) === 0) {
        var fromClass = cls.slice(IG_OFFER_PREFIX.length);
        if (fromClass) handle = fromClass;
      }
    }
    if (!handle && !planUpgrade) return null;
    return {
      handle: handle,
      planUpgrade: planUpgrade,
      code: attrOnRoots('data-fd-plan-upgrade-code'),
      percent: attrOnRoots('data-fd-plan-upgrade-percent'),
      savings: attrOnRoots('data-fd-plan-upgrade-savings'),
    };
  }

  function applyIntelligemsTrigger() {
    if (designMode) return;
    var trigger = readIntelligemsTrigger();
    if (!trigger) return;
    var key = [
      trigger.handle || '',
      trigger.planUpgrade ? '1' : '0',
      trigger.code || '',
      trigger.percent || '',
      trigger.savings || '',
    ].join('|');
    if (key === igClassApplied) return;
    igClassApplied = key;

    var extra = {
      planUpgrade: trigger.planUpgrade,
      code: trigger.code,
      percent: trigger.percent,
      savings: trigger.savings,
    };
    if (trigger.handle) {
      var offer = findOfferByHandleOrCode(catalog.offers, trigger.handle);
      if (offer && trigger.planUpgrade) {
        if (!extra.code) extra.code = String(offer.subCode || offer.otpCode || '').trim();
        if (!extra.percent && Number.isFinite(offer.ninetyDayPct) && offer.ninetyDayPct > 0) {
          extra.percent = offer.ninetyDayPct;
        }
        if (
          !extra.savings &&
          Number.isFinite(offer.ninetyDayPct) &&
          Number.isFinite(offer.subPct) &&
          offer.ninetyDayPct > offer.subPct
        ) {
          extra.savings = Math.round(offer.ninetyDayPct - offer.subPct);
        }
      }
      activateOffer(trigger.handle, extra);
      return;
    }
    if (trigger.planUpgrade) enablePlanUpgradeApi(extra);
  }

  function watchIntelligemsClass() {
    applyIntelligemsTrigger();
    if (typeof MutationObserver === 'undefined') return;
    var obs = new MutationObserver(applyIntelligemsTrigger);
    var opts = { attributes: true, attributeFilter: ['class', 'data-fd-offer', 'data-fd-plan-upgrade', 'data-fd-plan-upgrade-code', 'data-fd-plan-upgrade-percent', 'data-fd-plan-upgrade-savings'] };
    obs.observe(document.documentElement, opts);
    if (document.body) {
      obs.observe(document.body, opts);
    } else {
      document.addEventListener('DOMContentLoaded', function () {
        if (document.body) obs.observe(document.body, opts);
        applyIntelligemsTrigger();
      });
    }
  }

  watchIntelligemsClass();
})(typeof window !== 'undefined' ? window : this);
